import React, { useMemo } from "react";
import ShowContainer from "../../../mui/ShowContainer";
import { useTranslation } from "react-i18next";
import "./cashback.scss";
import { useAppSelector } from "../../../../store/index.ts";
import {
  ENUM_CASHBACK_STATUS,
  ENUM_USER_ACTIVE,
  ENUM_WELCOME_USER_STATUS
} from "../../../../store/enums.js";
import useBonusHelper from "../../../../hooks/other/useBonusHelper.js";
import { formatToDDMMYYYY, getNextWeekdayDate } from "../../../../utils/utils.js";
import BonusLockPlug from "../ui/lock_plug/BonusLockPlug.jsx";
import CashbackDay from "./CashbackDay.jsx";

const dayList = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const accruedDayList = [
  "on Monday",
  "on Tuesday",
  "on Wednesday",
  "on Thursday",
  "on Friday",
  "on Saturday",
  "on Sunday"
];

export default function Cashback() {
  const { t } = useTranslation();

  const currency = useAppSelector((state) => state.promotions.currency);
  const cashback = useAppSelector((state) => state.promotions.cashback);
  const isAuth = useAppSelector((state) => state.user.isAuth);
  const welcome = useAppSelector((state) => state.promotions.welcome);

  const isWelcomeActive =
    welcome?.statusForUser === ENUM_WELCOME_USER_STATUS.enabled ||
    welcome?.statusForUser === ENUM_WELCOME_USER_STATUS.accrued;

  const getAccruedDay = (data) => {
    if (!data) return "";
    try {
      const { date, weekDay } = data;

      if (!date && !weekDay) return t("instantly");
      if (!date) return t(accruedDayList[weekDay]);
      if (!weekDay) return formatToDDMMYYYY(date);

      const accrualInfo = getNextWeekdayDate(date, weekDay);
      const needWeek = t(accruedDayList[accrualInfo.weekDay]);

      return `${needWeek} (${accrualInfo.date})`;
    } catch (e) {
      return data?.date || "week day: " + data?.weekDay;
    }
  };

  const getUserStatus = (status, indx) => {
    const weekDay = t(dayList[indx]);

    if (status === ENUM_CASHBACK_STATUS.enabled) {
      return t("enabled");
    } else if (status === ENUM_CASHBACK_STATUS.waitActivate) {
      return t("awaits activation");
    } else if (status === ENUM_CASHBACK_STATUS.waitWeekDay) {
      return t("expecting day of the week", { weekDay });
    } else if (status === ENUM_CASHBACK_STATUS.waitWelcome) {
      return t("awaiting wagering of Welcome bonus");
    }
  };

  const { thenRequest, btnTextByStatus, getSendStatus } = useBonusHelper();

  const cashbackBonuses = useMemo(() => {
    const res = [];
    if (!cashback?.enabled) return res;

    cashback?.packets.forEach((pkg, indx) => {
      if (!pkg) return;

      const pkgInfo = {
        id: indx,
        title: t(dayList[indx]),
        stepper: pkg.values,
        toDeposit: pkg.toDeposit,
        accrued: getAccruedDay(pkg.accrualInfo),
        statusForUser: getUserStatus(pkg.statusForUser, indx)
      };

      if (pkg.userActivate?.enabled) {
        const activeStatus = pkg.userActivate.status;
        pkgInfo.status = activeStatus;
        pkgInfo.btnText = btnTextByStatus(activeStatus, pkg.statusForUser, "cashback");

        pkgInfo.isDisabled = activeStatus === ENUM_USER_ACTIVE.disabled;
        pkgInfo.sendStatus = getSendStatus(activeStatus);
      } else if (pkg.userActivate && !isAuth) {
        pkgInfo.btnText = "Activate";
      }

      res.push(pkgInfo);
    });
    return res;
  }, [cashback]);

  return (
    <ShowContainer condition={cashbackBonuses?.length}>
      <article className="cashback__article">
        <h3>{t("Cashback bonus")}</h3>
        <section className="bonuses__cashback">
          {cashbackBonuses.map((bonus) => (
            <CashbackDay
              key={bonus.id}
              bonus={bonus}
              indx={bonus.id}
              timeZone={cashback.timeZone}
              currency={currency}
              isWelcomeActive={isWelcomeActive}
              cashbackId={cashback.id}
              thenRequest={thenRequest}
            />
          ))}
        </section>
        <ShowContainer condition={isWelcomeActive}>
          <BonusLockPlug text="Cashback bonuses are not available while there is an available Welcome Bonus available." />
        </ShowContainer>
      </article>
    </ShowContainer>
  );
}
